import { Injectable } from "@angular/core";
import { IBizEntity, IBizEntityType } from "@optima-stride-ai/core";
import { FormStates, OffCanvasComponents, OffCanvasRightContentTypes } from "../../off-canvas/off-canvas-service/off-canvas-form.service";
import { ITab } from "@optima-stride-ai/optima-components";
import { ReplaySubject } from "rxjs";


@Injectable({
    providedIn: 'root'
})

export class AppStateService {
    private _selectedBizEntityTypeForEdit : IBizEntityType | null = null;
    private _activeDashboardId: string | null = null
    private _updatedDashboardLayout: any
    private _offCanvasRightContentType: OffCanvasRightContentTypes = OffCanvasRightContentTypes.None;
    private _offCanvasRightComponent: OffCanvasComponents = OffCanvasComponents.None;
    private _entityFormState: FormStates = FormStates.Idle;
    private _selectedBizEntityForEdit: IBizEntity | null = null;
    private _selectedTabConfig: ITab | null = null;
    private _unpinTabId: string | undefined = undefined;
    private _incomingBizEntitySelectionId: string | null = null;
    // Use ReplaySubject(1) to ensure late subscribers get the last emitted value
    // This handles the timing issue where ID is set before subscription is created
    private _incomingBizEntitySelectionIdSubject = new ReplaySubject<string | null>(1);
    incomingBizEntitySelectionId$ = this._incomingBizEntitySelectionIdSubject.asObservable();

    get selectedBizEntityForEdit(): IBizEntity | null {
        return this._selectedBizEntityForEdit;
    }
    
    set selectedBizEntityForEdit(value: IBizEntity | null) {
        this._selectedBizEntityForEdit = value;
    }
    
    get updatedDashboardLayout(): any {
        return this._updatedDashboardLayout;
    }
    
    set updatedDashboardLayout(value: any) {
        this._updatedDashboardLayout = value;
    }

    get selectedBizEntityTypeForEdit(): IBizEntityType | null {
        return this._selectedBizEntityTypeForEdit;
    }
    
    set selectedBizEntityTypeForEdit(value: IBizEntityType | null) {
        this._selectedBizEntityTypeForEdit = value;
    }

    get activeDashboardId(): string | null {
        return this._activeDashboardId;
    }
    
    set activeDashboardId(value: string | null) {
        this._activeDashboardId = value;
    }

    set entityFormState(state: FormStates) {
    // How and why FormState is managed?
    //
    //===== REASON=====
    //
    // Whether biz-entity is being added or edited is defined in one component and used in another component.
    // Example scenario : 
    //    Explorer Context menu (on the basis of item type clicked) determines whether biz-entity is being added or edited.
    //    So, code in context menu sets the state of the form to Add or Edit.
    //    The client of context menu, in this case - explorer component, uses the state to open the form in offCanvas.
    // We want to enforce that from code in different components, biz-entity form state is not changed without resetting it to idle first.
    // In other words we want to ensure One State's cycle is completed (FormState is set to new > Form Opens in new state > Form closes and set the state to idle) before another state is set.
    //
    //===== APPROACH & PITFALLS=====
    //
    // So, following check ensures that the state is not changed from one state to another without resetting it to idle first.
    // Problem in this approach - No two biz-entity forms be opened at the same time.
    // Considering YAGNI, postponing to handle this scenario.
    //
    // ====Planning to maintain states for multiple BizEntity forms???====
    // If we want to open two biz-entity forms at the same time in different states - One is in Edit and other is in Add, we need to change the approach.
    // If aforesaid requirement arise, we can change the approach to use a stack to maintain the state of each form.
    // Very important - See \app\views\forms\off-canvas-form\off-canvas-form.component.ts constructor. You can easily do it there with different levels.
    //===== IF YOU ENCOUNTER THIS ERROR - DEBUG LIKE SO====
    //
    // For ref see the working flow in following components -
    //        plan-tree.contextmenu.ts > onItemClicked() Sets the state of the form
    //        explorer.component.ts > openBizEntityFormInOffCanvas() Determines whether biz-entity form is to be opened and opens it in offcanvas
    //        off-canvas-form.service.ts > openOffCanvas() Opens the offCanvas
    //
    // ----STEP 1----
    // Follow the flow of the code - 
    //    Note where the FormState is set to New, Edit or View
    //    Note if there is some intermediate code (could be in multiple components) where opening offCanvas is cancelled (for any reason) but FormState is not reset to idle
    //    Note the code of the component from where the Form actually opens in offCanvas (openOffCanvas() function is called)
    //    Note whether OffCanvas' close button (top left) is resetting the state to idle (since this is at common place, it's less likely to be the cause of error)
    //    Note whether the Form component, where you have Create/Update button which closes the form, is resetting the state to idle or not.
    // During above cycle, if FormState is tried to be reset to another state without resetting it to idle, this error will be thrown.
    // ----STEP 2----
    // If you have added a new BizEntityComponent and form but somehow you do not have close form button in your form.
    // ----STEP 3----
    // Have you used openOffCanvas() and/or closeOffCanvas() to open/close BizEntity form in offcanvas? Have you made sure to give last optional param (compIsABizEntity) as true?

    // If you still can't figure out, reach out to the team
    if (this._entityFormState !== FormStates.Idle && state !== FormStates.Idle) {
      // Self-heal: reset to Idle BEFORE throwing so the app recovers on the next user action.
      // Without this, the stale non-Idle value persists and every subsequent form-opening click re-throws — app freezes.
      // The throw itself is preserved (canary for dev/QA); the state is left workable for prod resilience.
      this._entityFormState = FormStates.Idle;
      //CAUTION: DO NOT COMMENT THIS ERROR!!! If you are encountering this error see notes above to debug the issue. If you are still not able to resolve, reach out to the author of the method.
      throw new Error("Entity add/edit state is already in use. Please reset it to idle before setting it to another state. See detail in the code where error is thrown.")
    }
    this._entityFormState = state;
    // Caution: If you are doing anything in this function, read the comment above.
  }

  get entityFormState() {
    return this._entityFormState
  }

    set offCanvasRightContentType(contentType: OffCanvasRightContentTypes) {
    this._offCanvasRightContentType = contentType;
  }

  get offCanvasRightContentType() {
    return this._offCanvasRightContentType;
  }

  set offCanvasRightComponent(component: OffCanvasComponents) {
    this._offCanvasRightComponent = component;
  }
  
  get offCanvasRightComponent() {
    return this._offCanvasRightComponent;
  }

  // Newly added for tab management
    set selectedTabConfig(tabConfig: ITab | null) {
    this._selectedTabConfig = tabConfig;
  }
  
  get selectedTabConfig() {
    return this._selectedTabConfig;
  }
  set unpinTabId(tabId: string | undefined) {
    this._unpinTabId = tabId;
  }
  get unpinTabId() {
    return this._unpinTabId;
  }

  /**
   * Incoming business entity selection ID for "View in Explorer" navigation
   * Used to highlight an entity in the explorer after navigation from dashboard
   */
  get incomingBizEntitySelectionId(): string | null {
    return this._incomingBizEntitySelectionId;
  }
  
  set incomingBizEntitySelectionId(value: string | null) {
    this._incomingBizEntitySelectionId = value;
    this._incomingBizEntitySelectionIdSubject.next(value);
  }

}